import { useEffect } from 'react';

import { IconX } from '@tabler/icons-react';
import classnames from 'classnames';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';

import DatePeriodSelect, { getDateRangeOptions } from 'components/DatePeriodSelect';
import { Filter, useFiltersContext } from 'components/Filters';
import { InventoryPageKey } from 'pages/Inventory/InventoryPage/api/useSaveInventoryTableConfig/useSaveInventoryTableConfig';
import IconButton from '@lotlinx-aws/sparky-component-library/Sparky/IconButton';
import Text from '@lotlinx-aws/sparky-component-library/Sparky/Text';

import styles from './InventoryFilters.module.scss';
import { selectedDatePeriodAtom } from 'store/datePeriodAtom';

export interface InventoryFiltersProps {
  inventoryPageKey: InventoryPageKey;
  pageKey?: 'inventory' | 'vvo';
  isShow: boolean;

  handleClose(): void;

  boxShadow?: 'left' | 'right';
  hideCloseButton?: boolean;
  title: string;
}

/**
 * InventoryFilters
 */
export default function InventoryFilters({
  inventoryPageKey,
  isShow,
  pageKey = 'inventory',
  handleClose,
  boxShadow = 'left',
  hideCloseButton,
  title,
}: InventoryFiltersProps) {
  const { filtersState, filterConfigs } = useFiltersContext();
  const [, setSelectedDatePeriod] = useAtom(selectedDatePeriodAtom);

  const dateRangeOptions = getDateRangeOptions(false, 3, true);

  useEffect(() => {
    if (dayjs().date() === 1 && import.meta.env.VITE_APP_DEPLOY_ENV === 'demo') {
      setSelectedDatePeriod(
        dateRangeOptions.find((option) => option.value === 'Last 2 Months')?.value ?? 'MTD',
      );
    }
  }, []);

  return (
    <div
      className={classnames([
        styles.inventoryFilters,
        isShow && pageKey !== 'vvo' && styles.showFilters,
        isShow && pageKey === 'vvo' && styles.showFiltersVVO,
        boxShadow === 'right' ? styles.boxshadowRight : styles.boxshadowLeft,
      ])}
    >
      <div className={styles.filterHeader}>
        <Text fontWeight={7}>{title}</Text>
        {!hideCloseButton && (
          <IconButton onClick={handleClose} size='md' aria-label='Close filter sidebar'>
            <IconX size={20} />
          </IconButton>
        )}
      </div>
      <div
        className={classnames([
          styles.filtersBody,
          inventoryPageKey === 'live' && styles.withoutExtraFilters,
        ])}
      >
        {inventoryPageKey !== 'live' && (
          <div className={styles.extraFilters}>
            <DatePeriodSelect
              isVVODateRange={false}
              optionQuantity={12}
              monthToDate
              showPreviousMonths
            />
          </div>
        )}
        {filterConfigs
          .filter((filterConfig) => filterConfig.id !== 'search')
          .map((filter) => {
            if (
              (filter.id === 'activeFilterPills' && Object.keys(filtersState).length === 0) ||
              filter.excludeFromPage === inventoryPageKey
            ) {
              return null;
            }
            return (
              <Filter
                key={filter.id}
                config={filter}
                groupId='inventory'
                contentClassName={
                  filter.id === 'activeFilterPills' ? styles.activeFiltersPills : undefined
                }
              />
            );
          })}
      </div>
    </div>
  );
}
